// @flow
import React, { useState, useEffect } from 'react';
// Components
import { ColorRow, RefreshModal } from '../../common';
import BigClubPreview from './BigClubPreview';
import CartPreview from './CartPreview';
import AddressPane from './address/AddressPane';
import DeliveryPane from './delivery/DeliveryPane';
import PaymentMethodPane from './payment/PaymentPane';
import ResumePane from './resume/ResumePane';
// Styles
import './CheckoutPage.css';
// Types
import { BigClubSettings } from '../../preferences/types';
import type { ShippingRate } from '../../types';
import type { UserAddress } from '../../user';
import type {
  BigClubInfo,
  CreditCard,
  LineItem,
  OrderAddress,
  PaymentMethod,
  DeliveryOptions,
} from '../types';
// Helpers
import { scrollToTop } from '../../common';
import { useUtmManager } from '../../common/customHooks/useUtmManager';

/**
 * PROPS AND STATE
 */
export type Props = {
  orderNumber: string,
  getBlends: (orderNumber: string) => void,
  blendsByOrderNumber: (orderNumber: string) => void,
  // Order management
  orderState: 'cart' | 'address' | 'delivery' | 'payment' | 'confirm',
  loading: boolean,
  transitioningLoading: boolean,
  // General information
  items: LineItem[],
  totalPrice: string,
  totalDiscount: string,
  totalItemPrice: string,
  shippingPrice: string,
  numberOfItems: number,
  promotions: any,
  // Errors
  addressErrors: string[],
  deliveryErrors: string[],
  promotionErrors: string[],
  paymentErrors: string[],
  dismissErrors: () => void,
  // Address
  orderShippingAddress: ?OrderAddress, // Selected address from the order. Type is different from others.
  addresses: UserAddress[],
  newAddress: (address: UserAddress) => Promise<?Error>,
  updateAddress: (address: UserAddress) => Promise<?Error>,
  deleteAddress: (addressId: number) => void,
  submitAddress: UserAddress => void,
  addressesDeleteByIdLoading: (id: number) => boolean,
  addressesEditingLoading: boolean,
  addressesAddingLoading: boolean,
  addressesLoading: boolean,
  // Delivery dates
  shippingRates: any,
  submitShippingRate: ShippingRate => void,
  orderDelivery: ShippingRate,
  // Payment
  bigClubInfo: BigClubInfo,
  creditCards: CreditCard[],
  paymentMethods: PaymentMethod[],
  submitPayment: (CreditCard | PaymentMethod) => void,
  orderPayment: PaymentMethod,
  // promotion
  submitPromotion: (string[]) => void,
  // Confirmation
  submitConfirmation: () => void,
  bigClubSettings: BigClubSettings,
  transitionToState: string => void,
  deliveryOptionsCount: DeliveryOptions,
};

const CheckoutPage = (props: Props) => {
  const { clearVisitorLogData } = useUtmManager();

  const [bigClubCreditAmount, setBigClubCreditAmount] = useState(0);
  const [useBigClubCredit, setUseBigClubCredit] = useState(false);
  const [paymentState, setPaymentState] = useState(null);
  const [showResume, setShowResume] = useState(false);

  const handleClearPaymentState = () => {
    if (showResume || !!paymentState) {
      setPaymentState(null);
      setShowResume(false);
    }
  };

  const handlePayment = (issueResolution?: string | null) => {
    const { paymentMethod, paymentSource, existingCard, bigClubCreditAmount } =
      paymentState;

    clearVisitorLogData();

    props.submitPayment(
      paymentMethod,
      paymentSource,
      existingCard,
      bigClubCreditAmount,
      issueResolution || null,
    );
  };

  const handleTransition = (transitionTo: 'address' | 'cart' = '') => {
    if (transitionTo) {
      props.transitionToState(transitionTo);
    }
    handleClearPaymentState();
    scrollToTop();
  };

  const handleSelectPayment = (
    paymentMethod,
    paymentSource,
    existingCard,
    bigClubCreditAmount,
  ) => {
    setPaymentState({
      paymentMethod,
      paymentSource,
      existingCard,
      bigClubCreditAmount,
    });
    setShowResume(true);
  };

  const hasOnlyGiftCards =
    props.items.length > 0 && !props.items.some(item => item.giftCard == null);

  useEffect(() => {
    props.dismissErrors();
  }, []);

  return (
    <div className="check-out-page-container">
      <RefreshModal />
      <ColorRow color="red" text="CHECKOUT" />
      <div className="check-out-page-main">
        <div className="check-out-page-pane">
          <AddressPane
            transitionToAddress={() => {
              handleTransition('cart');
            }}
            errors={props.addressErrors}
            active={props.orderState === 'address'}
            isCompleted={
              ['delivery', 'confirm', 'payment'].indexOf(props.orderState) > -1
            }
            selected={props.orderShippingAddress}
            onSubmit={props.submitAddress}
            loading={props.transitioningLoading}
            takeoutAddress={props.takeoutAddress}
            shippingRates={props.shippingRates}
            orderNumber={props.orderNumber}
            // From address connector
            addresses={props.addresses}
            addressesLoading={props.addressesLoading}
            editingLoading={props.addressesEditingLoading}
            addingLoading={props.addressesAddingLoading}
            deletingLoading={props.addressesDeleteByIdLoading}
            onNew={props.newAddress}
            onEdit={props.updateAddress}
            onDelete={props.deleteAddress}
            dismissErrors={props.dismissErrors}
            deliveryOptionsCount={props.deliveryOptionsCount}
            hasOnlyGiftCards={hasOnlyGiftCards}
          />
          {!hasOnlyGiftCards && (
            <DeliveryPane
              transitionToDelivery={() => {
                handleTransition('address');
              }}
              errors={props.deliveryErrors}
              selected={
                props.orderState !== 'address' ? props.orderDelivery : null
              }
              shippingRates={props.shippingRates}
              orderAddress={props.orderShippingAddress}
              onSubmit={props.submitShippingRate}
              active={['delivery'].indexOf(props.orderState) > -1}
              isCompleted={
                ['payment', 'confirm'].indexOf(props.orderState) > -1
              }
              loading={props.transitioningLoading}
              dismissErrors={props.dismissErrors}
              takeoutAddress={props.takeoutAddress}
            />
          )}
          <PaymentMethodPane
            transitionToPayment={() => {
              handleClearPaymentState();
            }}
            orderNumber={props.orderNumber}
            adjustmentTotal={props.adjustmentTotal}
            promotions={props.promotions}
            promotionsLoading={props.promotionsLoading}
            promotionErrors={props.promotionErrors}
            errors={props.paymentErrors}
            selected={props.orderPayment}
            bigClubCreditAmount={bigClubCreditAmount}
            useBigClubCredit={useBigClubCredit}
            creditCards={props.creditCards}
            paymentMethods={props.paymentMethods}
            couponCodesApplied={props.couponCodesApplied}
            submitPayment={handleSelectPayment}
            submitPromotion={props.submitPromotion}
            removeCouponCode={props.removeCouponCode}
            active={
              ['payment', 'confirm'].indexOf(props.orderState) > -1 &&
              !showResume
            }
            isCompleted={showResume}
            loading={props.transitioningLoading}
            dismissErrors={props.dismissErrors}
            totalPrice={props.totalPrice}
            cashbackMinOrderPriceToEarn={
              props.bigClubSettings?.cashback?.cashbackMinOrderPriceToEarn
            }
            hasOnlyGiftCards={hasOnlyGiftCards}
            isApplyingPromotion={props.applyingPromotionLoading}
          />
          <ResumePane
            active={
              showResume &&
              ['payment', 'confirm'].indexOf(props.orderState) > -1
            }
            loading={props.transitioningLoading}
            submitConfirmation={issueResolution => {
              handlePayment(issueResolution);
            }}
            errors={props.paymentErrors}
            orderNumber={props.orderNumber}
            blendsByOrderNumber={props.blendsByOrderNumber}
            items={props.items}
            orderPayment={paymentState || null}
            orderDelivery={props.orderDelivery || null}
            orderAddress={props.orderShippingAddress || null}
            adjustmentTotal={props.adjustmentTotal}
            totalPrice={props.totalPrice}
            shipTotal={props.shippingPrice}
            availableStoreCredits={props.availableStoreCredits}
            appliedStoreCredits={props.appliedStoreCredits}
            useBigClubCredit={useBigClubCredit}
            bigClubInfo={props.bigClubInfo}
            bigClubCreditAmount={bigClubCreditAmount}
            itemTotal={props.totalItemPrice}
            dismissErrors={props.dismissErrors}
            transitionToAddress={() => {
              handleTransition('cart');
            }}
            transitionToDelivery={() => {
              handleTransition('address');
            }}
            transitionToPayment={() => {
              handleTransition();
            }}
            hasOnlyGiftCards={hasOnlyGiftCards}
          />
        </div>
        <div className="check-out-page-column">
          <BigClubPreview
            handleApplyBigClubCredit={(value: number) => {
              setBigClubCreditAmount(value);
            }}
            bigClubSettings={props.bigClubSettings}
            bigClubCreditAmount={bigClubCreditAmount}
            bigClubInfo={props.bigClubInfo}
            orderNumber={props.orderNumber}
            setUseBigClubCredit={(value: boolean) => {
              setUseBigClubCredit(value);
            }}
            useBigClubCredit={useBigClubCredit}
          />
          <CartPreview
            transitionToAddress={() => {
              handleTransition('cart');
            }}
            transitionToDelivery={() => {
              handleTransition('address');
            }}
            transitionToPayment={() => {
              handleTransition();
            }}
            orderPayment={paymentState || null}
            orderDelivery={props.orderDelivery || null}
            orderShippingAddress={props.orderShippingAddress || null}
            items={props.items}
            useBigClubCredit={useBigClubCredit}
            bigClubInfo={props.bigClubInfo}
            bigClubCreditAmount={bigClubCreditAmount}
            orderNumber={props.orderNumber}
            blendsByOrderNumber={props.blendsByOrderNumber}
            totalPrice={props.totalPrice}
            itemTotal={props.totalItemPrice}
            shipTotal={props.shippingPrice}
            adjustmentTotal={props.adjustmentTotal}
            availableStoreCredits={props.availableStoreCredits}
            appliedStoreCredits={props.appliedStoreCredits}
            canSubmit={['payment', 'confirm'].indexOf(props.orderState) > -1}
            loading={
              props.transitioningLoading && props.orderState === 'confirm'
            }
            onSubmit={() => handlePayment()}
            hasOnlyGiftCards={hasOnlyGiftCards}
          />
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
